import React, { useState, useEffect } from 'react';
import { Play, Pause, RotateCcw, Eye, Brain, Zap, Search } from 'lucide-react';

const ConstructionAgentsDemo = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [scenario, setScenario] = useState('claims');

  const scenarios = {
    claims: {
      title: "Claims Processing Agent",
      goal: "Process water damage claim from roofing project",
      steps: [
        {
          phase: "Perceive",
          icon: <Eye className="w-6 h-6" />,
          description: "Receiving claim: Water damage at Sunset Mall roof project, $45K estimate",
          details: "Ingests: photos, contractor reports, weather data, policy details, project timeline",
          color: "bg-green-100 border-green-300"
        },
        {
          phase: "Reason",
          icon: <Brain className="w-6 h-6" />,
          description: "Analyzing claim validity against policy terms and weather records",
          details: "Cross-references: policy coverage, weather reports, similar claims, contractor history",
          color: "bg-blue-100 border-blue-300"
        },
        {
          phase: "Act",
          icon: <Zap className="w-6 h-6" />,
          description: "Approving claim, scheduling adjuster, and initiating payment",
          details: "Executes: claim approval, adjuster assignment, payment processing, contractor notification",
          color: "bg-purple-100 border-purple-300"
        },
        {
          phase: "Observe",
          icon: <Search className="w-6 h-6" />,
          description: "Monitoring repair progress and updating risk profile",
          details: "Tracks: repair completion, quality inspections, updated project risk score",
          color: "bg-orange-100 border-orange-300"
        }
      ]
    },
    risk: {
      title: "Risk Assessment Agent",
      goal: "Evaluate new high-rise construction project",
      steps: [
        {
          phase: "Perceive",
          icon: <Eye className="w-6 h-6" />,
          description: "Analyzing project specs: 40-story building, downtown Chicago, $200M value",
          details: "Reviews: blueprints, contractor credentials, location data, historical weather patterns",
          color: "bg-green-100 border-green-300"
        },
        {
          phase: "Reason",
          icon: <Brain className="w-6 h-6" />,
          description: "Calculating risk scores based on height, location, and contractor history",
          details: "Evaluates: seismic risk, wind exposure, contractor safety record, local regulations",
          color: "bg-blue-100 border-blue-300"
        },
        {
          phase: "Act",
          icon: <Zap className="w-6 h-6" />,
          description: "Setting premium at $850K with specific safety requirements",
          details: "Implements: premium calculation, safety mandates, inspection schedule, coverage limits",
          color: "bg-purple-100 border-purple-300"
        },
        {
          phase: "Observe",
          icon: <Search className="w-6 h-6" />,
          description: "Monitoring construction progress and adjusting risk assessments",
          details: "Tracks: safety incidents, weather impacts, timeline adherence, inspection results",
          color: "bg-orange-100 border-orange-300"
        }
      ]
    },
    safety: {
      title: "Safety Monitoring Agent",
      goal: "Monitor job site safety compliance",
      steps: [
        {
          phase: "Perceive",
          icon: <Eye className="w-6 h-6" />,
          description: "Processing site photos showing workers without hard hats in Zone 3",
          details: "Analyzes: real-time camera feeds, PPE detection, worker locations, hazard zones",
          color: "bg-green-100 border-green-300"
        },
        {
          phase: "Reason",
          icon: <Brain className="w-6 h-6" />,
          description: "Identifying safety violation and assessing immediate risk level",
          details: "Evaluates: violation severity, worker proximity to hazards, historical incident data",
          color: "bg-blue-100 border-blue-300"
        },
        {
          phase: "Act",
          icon: <Zap className="w-6 h-6" />,
          description: "Alerting safety manager and logging violation for premium adjustment",
          details: "Triggers: immediate alert, work stoppage if critical, violation documentation",
          color: "bg-purple-100 border-purple-300"
        },
        {
          phase: "Observe",
          icon: <Search className="w-6 h-6" />,
          description: "Confirming workers equipped PPE and updating safety score",
          details: "Validates: compliance restoration, safety training completion, risk score updates",
          color: "bg-orange-100 border-orange-300"
        }
      ]
    }
  };

  const currentScenario = scenarios[scenario];

  useEffect(() => {
    let interval;
    if (isRunning) {
      interval = setInterval(() => {
        setCurrentStep(prev => {
          if (prev >= currentScenario.steps.length - 1) {
            setIsRunning(false);
            return prev;
          }
          return prev + 1;
        });
      }, 2500);
    }
    return () => clearInterval(interval);
  }, [isRunning, currentScenario.steps.length]);

  const resetDemo = () => {
    setCurrentStep(0);
    setIsRunning(false);
  };

  const toggleRunning = () => {
    if (currentStep >= currentScenario.steps.length - 1) {
      resetDemo();
    } else {
      setIsRunning(!isRunning);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-gradient-to-br from-blue-50 to-orange-100 min-h-screen">
      <div className="bg-white rounded-xl shadow-lg p-8">
        <h1 className="text-3xl font-bold text-center mb-2 text-gray-800">Construction Insurance AI Agents</h1>
        <p className="text-center text-gray-600 mb-6">See how AI agents automate insurance workflows 24/7</p>
        
        {/* Scenario Selector */}
        <div className="flex justify-center mb-8">
          <div className="bg-gray-100 rounded-lg p-1 flex gap-1">
            {Object.entries(scenarios).map(([key, scenario]) => (
              <button
                key={key}
                onClick={() => {
                  setScenario(key);
                  resetDemo();
                }}
                className={`px-4 py-2 rounded-md transition-all ${
                  scenario === key 
                    ? 'bg-orange-500 text-white shadow-md' 
                    : 'hover:bg-gray-200 text-gray-700'
                }`}
              >
                {scenario.title}
              </button>
            ))}
          </div>
        </div>

        {/* Current Scenario Info */}
        <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white p-6 rounded-lg mb-8">
          <h2 className="text-2xl font-bold mb-2">{currentScenario.title}</h2>
          <p className="text-orange-100 text-lg">🎯 Goal: {currentScenario.goal}</p>
          <p className="text-orange-200 text-sm mt-2">
            ⏰ This agent works 24/7, processing requests even during off-hours
          </p>
        </div>

        {/* Controls */}
        <div className="flex justify-center gap-4 mb-8">
          <button
            onClick={toggleRunning}
            className="flex items-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            {isRunning ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            {currentStep >= currentScenario.steps.length - 1 ? 'Restart' : (isRunning ? 'Pause' : 'Start')} Agent
          </button>
          
          <button
            onClick={resetDemo}
            className="flex items-center gap-2 px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            <RotateCcw className="w-5 h-5" />
            Reset
          </button>
        </div>

        {/* Workflow Steps */}
        <div className="space-y-6">
          {currentScenario.steps.map((step, index) => {
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;

            return (
              <div
                key={index}
                className={`border-2 rounded-lg p-6 transition-all duration-500 ${
                  isActive 
                    ? `${step.color} shadow-lg scale-105 border-dashed` 
                    : isCompleted 
                      ? 'bg-gray-50 border-gray-300 opacity-75'
                      : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-full ${
                    isActive ? 'bg-white shadow-md' : isCompleted ? 'bg-green-100' : 'bg-gray-100'
                  }`}>
                    {step.icon}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className={`text-xl font-bold ${
                        isActive ? 'text-gray-800' : isCompleted ? 'text-gray-600' : 'text-gray-400'
                      }`}>
                        Step {index + 1}: {step.phase}
                      </h3>
                      
                      {isActive && (
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                          <span className="text-sm text-orange-600 font-medium">Processing</span>
                        </div>
                      )}
                      
                      {isCompleted && (
                        <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        </div>
                      )}
                    </div>
                    
                    <p className={`text-lg mb-3 ${
                      isActive ? 'text-gray-800' : isCompleted ? 'text-gray-600' : 'text-gray-400'
                    }`}>
                      {step.description}
                    </p>
                    
                    <p className={`text-sm ${
                      isActive ? 'text-gray-600' : isCompleted ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                      {step.details}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Key Benefits for Insurance */}
        <div className="mt-8 bg-gradient-to-r from-amber-50 to-yellow-100 border border-amber-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-amber-800 mb-2">🔑 Why Construction Insurers Need AI Agents</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-amber-700 mb-3">⚡ Efficiency Gains</h4>
              <ul className="text-sm text-amber-600 space-y-1">
                <li>• Process claims 80% faster</li>
                <li>• 24/7 monitoring without breaks</li>
                <li>• Reduce manual paperwork by 90%</li>
                <li>• Instant risk assessments</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-amber-700 mb-3">💰 Cost Savings</h4>
              <ul className="text-sm text-amber-600 space-y-1">
                <li>• Early fraud detection saves millions</li>
                <li>• Prevent incidents through real-time monitoring</li>
                <li>• Optimize premium pricing accuracy</li>
                <li>• Reduce claims adjustment costs</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Industry Stats */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-red-50 p-4 rounded-lg border border-red-200 text-center">
            <div className="text-2xl font-bold text-red-700 mb-1">$15B</div>
            <div className="text-sm text-red-600">Annual construction insurance fraud</div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
            <div className="text-2xl font-bold text-blue-700 mb-1">47%</div>
            <div className="text-sm text-blue-600">Claims processing time reduction with AI</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
            <div className="text-2xl font-bold text-green-700 mb-1">1 in 3</div>
            <div className="text-sm text-green-600">Construction accidents preventable with monitoring</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConstructionAgentsDemo;
